import React, { useState, useEffect } from 'react';
import { urlUtils } from '../utils/urlUtils';

const QuickAddForm = ({ onSubmit, loading = false }) => {
  const [formData, setFormData] = useState({
    url: '',
    title: '',
    description: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [isCheckingClipboard, setIsCheckingClipboard] = useState(false);

  // Auto-check clipboard on mount
  useEffect(() => {
    const initializeForm = async () => {
      await checkClipboardForURL();
    };
    initializeForm();
  }, []);

  const handleClearInput = (fieldName) => {
    setFormData(prev => ({ ...prev, [fieldName]: '' }));
  };

  const checkClipboardForURL = async () => {
    setIsCheckingClipboard(true);
    try {
      const clipboardURL = await urlUtils.checkClipboard();
      if (clipboardURL) {
        const autoTitle = await urlUtils.fetchPageTitle(clipboardURL);
        setFormData(prev => ({ 
          ...prev, 
          url: clipboardURL,
          title: prev.title || autoTitle
        }));
      }
    } catch (error) {
      // Clipboard access failed - this is expected in some browsers/contexts
    } finally {
      setIsCheckingClipboard(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let processedUrl = formData.url.trim();
    let urlModified = false;

    if (processedUrl && 
        !processedUrl.match(/^https?:\/\//i) && 
        processedUrl.includes('.') && 
        !processedUrl.includes(' ') && 
        !processedUrl.startsWith('/')) {
      processedUrl = `https://${processedUrl}`;
      urlModified = true;
    }

    if (urlModified) {
      setFormData(prev => ({ ...prev, url: processedUrl }));
    }
    
    if (!processedUrl) { // Check after potential modification
      alert('Please enter a URL');
      return;
    }

    if (!urlUtils.isValidURL(processedUrl)) {
      alert('Please enter a valid URL');
      return;
    }

    const finalTitle = formData.title || await urlUtils.fetchPageTitle(processedUrl);
    
    const bookmarkData = {
      ...formData,
      url: urlUtils.formatURL(processedUrl),
      title: finalTitle,
      tags: formData.tags.filter(tag => tag.trim())
    };

    try {
      await onSubmit(bookmarkData);
      // Reset form
      setFormData({
        url: '',
        title: '',
        description: '',
        tags: []
      });
      setTagInput('');
    } catch (error) {
      console.error('Error submitting bookmark:', error);
    }
  };

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Check if URL field has content
  const hasUrl = formData.url.trim().length > 0;

  return (
    <div className="bg-gray-100 rounded-xl shadow-lg border border-border px-6 pt-4 pb-6 mb-6 hover:shadow-xl transition-all duration-200"> {/* Changed bg-gray-50 to bg-gray-100 */}
      <div className="flex justify-between items-center mb-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Quick Add Bookmark</h2>
        </div>
        <div className="flex items-center gap-3">
          {isCheckingClipboard && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
              <span className="text-xs text-gray-500">Checking clipboard...</span>
            </div>
          )}
          {hasUrl && (
            <button
              type="submit"
              form="quickAddForm" // Link to the form
              disabled={loading}
              className="flex items-center gap-2 p-2 text-gray-600 hover:text-success hover:bg-emerald-50 rounded-lg transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              title={loading ? 'Saving...' : 'Save Bookmark'}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-success border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              )}
              <span className="hidden sm:inline text-sm">Save</span> {/* Added Save text, hidden on mobile */}
            </button>
          )}
        </div>
      </div>

      <form id="quickAddForm" onSubmit={handleSubmit} className="space-y-3">
        {/* URL Field - Always Visible */}
        <div className="flex gap-2 items-start">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Paste URL here..."
              value={formData.url}
              onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
              className="w-full px-4 py-3 text-sm bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 placeholder-gray-400 pr-10"
              autoFocus
            />
            {formData.url && (
              <button
                type="button"
                onClick={() => handleClearInput('url')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                title="Clear URL"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={checkClipboardForURL}
            className="flex-shrink-0 flex items-center justify-center w-10 h-10 sm:justify-start sm:w-32 sm:px-3 sm:py-2 sm:gap-2 text-gray-600 hover:text-primary hover:bg-teal-50 rounded-lg transition-all duration-200"
            title="Check clipboard for URL"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="8" y="2" width="8" height="4" rx="1" ry="1" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}></rect>
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}></path>
            </svg>
            <span className="hidden sm:inline text-sm">Clipboard</span>
          </button>
        </div>

        {/* Tags, Title, and Description Fields - Show only when URL has value */}
        {hasUrl && (
          <div className="space-y-3 pt-3 mt-3 border-t border-gray-100">
            {/* Tags Input and Display */}
            <div>
              <input
                type="text"
                placeholder="Add tags (press Enter or comma to add)"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleTagKeyPress}
                onBlur={addTag}
                className="w-full px-4 py-3 text-sm bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 placeholder-gray-400"
              />
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary text-white"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 text-white hover:text-gray-200"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Title Input */}
            <div className="relative">
              <input
                type="text"
                placeholder="Bookmark title (auto-generated if empty)"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-3 text-sm bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 placeholder-gray-400 pr-10"
              />
              {formData.title && (
                <button
                  type="button"
                  onClick={() => handleClearInput('title')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                  title="Clear title"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>

            {/* Description Textarea */}
            <textarea
              placeholder="Optional description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-4 py-3 text-sm bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 placeholder-gray-400 resize-none"
            />
          </div>
        )}
      </form>
    </div>
  );
};

export default QuickAddForm;
